import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import { loadRazorpayCheckout } from '../../lib/razorpay';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, CreditCard, Wallet, CheckCircle2 } from 'lucide-react';

const FASTagRecharge = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const quickAmounts = [200, 500, 1000, 2000, 5000];

  const handleRecharge = async () => {
    if (!vehicleNumber.trim()) {
      toast({ title: 'Enter Vehicle Number', description: 'Please enter your vehicle number', variant: 'destructive' });
      return;
    }
    const amt = parseFloat(amount);
    if (!amount || amt < 100) {
      toast({ title: 'Invalid Amount', description: 'Minimum recharge amount is ₹100', variant: 'destructive' });
      return;
    }

    setIsProcessing(true);
    try {
      // 1) Load Razorpay Checkout JS
      const loaded = await loadRazorpayCheckout();
      if (!loaded) {
        toast({ title: 'Network Error', description: 'Could not load Razorpay. Please try again.', variant: 'destructive' });
        setIsProcessing(false);
        return;
      }

      // 2) Create order on the server
      const { data: order } = await api.post('/api/fastag/razorpay/create-order', {
        vehicle_number: vehicleNumber.trim().toUpperCase(),
        amount: amt,
      });

      // 3) Open Razorpay Checkout
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'BookMyDriver',
        description: `FASTag recharge for ${order.vehicle_number}`,
        order_id: order.order_id,
        prefill: {
          name: user?.name || 'BookMyDriver User',
          contact: user?.phone || '',
        },
        notes: { vehicle_number: order.vehicle_number },
        theme: { color: '#000000' },
        handler: async (response) => {
          // 4) Server-side signature verification + balance credit
          try {
            const { data: verifyRes } = await api.post('/api/fastag/razorpay/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              vehicle_number: order.vehicle_number,
              amount: amt,
            });
            updateUser({ fastagBalance: verifyRes.new_balance });
            toast({
              title: 'Recharge Successful!',
              description: `₹${amt} credited. New balance: ₹${verifyRes.new_balance}`,
              duration: 5000,
            });
            setVehicleNumber('');
            setAmount('');
          } catch (err) {
            const detail = err?.response?.data?.detail;
            toast({
              title: 'Payment Verification Failed',
              description: typeof detail === 'string' ? detail : 'Please contact support',
              variant: 'destructive',
            });
          } finally {
            setIsProcessing(false);
          }
        },
        modal: {
          ondismiss: () => setIsProcessing(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        toast({
          title: 'Payment Failed',
          description: response?.error?.description || 'Please try again',
          variant: 'destructive',
        });
        setIsProcessing(false);
      });
      rzp.open();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: 'Recharge Failed',
        description: typeof detail === 'string' ? detail : 'Please try again',
        variant: 'destructive',
      });
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/user/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold">FASTag Recharge</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <Card className="p-6 mb-6 bg-gradient-to-br from-primary-dark to-primary text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 mb-1">Current FASTag Balance</p>
              <p className="text-4xl font-bold" data-testid="fastag-current-balance">₹{user?.fastagBalance || 0}</p>
            </div>
            <CreditCard className="w-16 h-16 text-white/50" />
          </div>
        </Card>

        <Card className="p-6 mb-6">
          <h2 className="text-xl font-semibold mb-6">Recharge Details</h2>

          <div className="space-y-4">
            <div>
              <Label htmlFor="vehicle">Vehicle Number</Label>
              <Input
                id="vehicle"
                placeholder="e.g., DL 01 AB 1234"
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                className="h-12"
                data-testid="fastag-vehicle-input"
              />
            </div>

            <div>
              <Label>Select Amount</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {quickAmounts.map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setAmount(amt.toString())}
                    className={`p-3 rounded-lg border-2 font-semibold transition-all ${
                      amount === amt.toString()
                        ? 'border-primary bg-muted'
                        : 'border-border hover:border-border'
                    }`}
                  >
                    ₹{amt}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="amount">Or Enter Custom Amount</Label>
              <Input
                id="amount"
                type="number"
                placeholder="Enter amount (Min: ₹100)"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-12"
                min="100"
                data-testid="fastag-amount-input"
              />
            </div>
          </div>
        </Card>

        <Card className="p-6 mb-6 bg-accent/10 border-accent/30">
          <div className="flex gap-3">
            <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-primary mb-1">Secured by Razorpay</h3>
              <p className="text-sm text-primary">
                Payment is processed by Razorpay. UPI / Cards / Netbanking / Wallets all supported.
              </p>
            </div>
          </div>
        </Card>

        <Button
          size="lg"
          className="w-full bg-primary hover:bg-primary-dark text-lg"
          onClick={handleRecharge}
          disabled={isProcessing}
          data-testid="fastag-recharge-btn"
        >
          {isProcessing ? (
            <span className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Processing...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Recharge FASTag
            </span>
          )}
        </Button>

        <p className="text-center text-sm text-muted-foreground mt-4">
          By proceeding, you agree to our terms and conditions
        </p>
      </div>
    </div>
  );
};

export default FASTagRecharge;
